import { ChatKit as Chat, useChatKit } from '@openai/chatkit-react'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'
import { ChatKitArrowIcon, ChatKitChatIcon } from './icons'
import { LoadingIcon } from '@systemeio/ui/icons'
import { useChatKitActions } from './hooks'
import useMediaQuery from 'shared/hooks/use-media-query'

export const ChatKit = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { isMD } = useMediaQuery()

  const { getClientSecret, escalateSupportRequest } = useChatKitActions()

  const { control } = useChatKit({
    api: {
      async getClientSecret() {
        try {
          setIsLoading(true)

          const { clientSecret } = await getClientSecret()

          return clientSecret
        } finally {
          setIsLoading(false)
        }
      },
    },
    onClientTool: async (invocation: { name: string; params: Record<string, unknown> }) => {
      if (invocation.name === 'create_support_request') {
        try {
          await escalateSupportRequest(invocation.params)

          return { success: true }
        } catch (error) {
          return { success: false }
        }
      }

      return { success: false }
    },
  })

  useEffect(() => {
    if (isOpen && !isMD) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen, isMD])

  return (
    <>
      <Script src="https://cdn.platform.openai.com/deployments/chatkit/chatkit.js" async />
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={twMerge(
          'bg-text-primary fixed -right-2 bottom-16 z-50 flex h-12 w-8 items-center justify-center rounded-l-md pr-2 shadow-lg md:w-14',
          'transition-transform duration-300 ease-in-out hover:scale-110 hover:shadow-xl',
          'active:scale-90 md:active:scale-100',
          isLoading && 'animate-pulse',
        )}
        disabled={isLoading}
      >
        <span className="relative h-full w-full">
          <span
            className={twMerge(
              'absolute inset-0 flex transform items-center justify-center transition-all duration-300 ease-in-out',
              isOpen || isLoading ? 'scale-0 opacity-0' : 'scale-100 opacity-100',
            )}
          >
            <ChatKitChatIcon width={isMD ? 24 : 16} />
          </span>
          <span
            className={twMerge(
              'absolute inset-0 flex transform items-center justify-center transition-all duration-300 ease-in-out',
              isOpen && !isLoading ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
            )}
          >
            <ChatKitArrowIcon />
          </span>
          <span
            className={twMerge(
              'absolute inset-0 flex transform items-center justify-center transition-all duration-300 ease-in-out',
              isLoading ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
            )}
          >
            <span className={twJoin('relative left-2')}>
              <LoadingIcon style={{ width: isMD ? 24 : 16 }} />
            </span>
          </span>
        </span>
      </button>
      {isOpen && (
        <div
          className={twJoin(
            ' fixed h-full w-full sm:w-auto sm:min-w-[320px] md:h-[40%]',
            isMD ? 'right-10 bottom-14' : 'left-0 bottom-0 right-0 top-0',
          )}
        >
          <Chat control={control} />
        </div>
      )}
    </>
  )
}
